import Column from '@amzn/meridian/column';
import Pagination from '@amzn/meridian/pagination';
import Row from '@amzn/meridian/row';
import Table, {
  TableActionBar,
  TableCell,
  TableRow,
} from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';

import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isFeatureEnabled, Feature } from '../../../../config/FeaturesConfig';
import { partitionArray } from '../../../../Util/Helper';

import {
  ConsolidatedContact,
  ContactDetails,
  ContactViewTabObject,
  defaultContactDetails,
} from '../../../../api/CoachingNotes/schema';
import useCustomTracking from '../../../../Util/useCustomTracking';
import ModalForm from '../../Modal/ContactModal/Form';
import { RecommendedTabFilter } from './Filter/RecommendedTabFilter';
import {
  RECOMMEND_SIZE,
  RECOMMEND_SIZE_WITH_SA,
  RECOMMEND_KUDO_COUNT,
  PAGINATION_SIZE_WITH_SA_RECOMMENDED,
  RECOMMEND_KUDO_COUNT_WITH_SA,
  customSort,
  EmptyFilterView,
  formatDuration,
  getChannelI18nKey,
  getOwnershipI18nKey,
  mapToActionStatus,
  NoContactView,
  NOT_FOUND_LABEL,
  SortableColumn,
  SortableDirection,
  StatusText,
  TabProps,
} from './TabHelper';
import MoreInfoIcon from '../../../../commons/MoreInfoIcon';
import {
  isCCXCategory,
  isConcessionComplianceCategory,
  isKudosCategory,
  isHoldRelatedCategory,
} from './Utils/FilterSortHelper';
export const RecommendedTab: React.FC<TabProps> = ({
  contacts,
  username,
  userID,
  sourcePage,
}: TabProps): React.ReactElement => {
  const intl = useIntl();
  const [sortColumn, setSortColumn] = useState(SortableColumn.INIT);
  const [sortDirection, setSortDirection] = useState(
    SortableDirection.DECENDING
  );
  // Feature Flag
  const concessionFeatureEnabled = isFeatureEnabled(
    Feature.ENABLE_CONCESSION_COMPLIANCE
  );
  const SACoaching = isFeatureEnabled(Feature.SA_COACHING);
  const hideHoldRecommendations = isFeatureEnabled(
    Feature.HIDE_HOLD_RECOMMENDATIONS
  );

  // First filter out hold-related contacts if feature flag is enabled
  const holdFilteredContacts = hideHoldRecommendations
    ? contacts.filter(c => !isHoldRelatedCategory(c))
    : contacts;

  // Filter out CCX-related contacts when enableCCXRemoval is enabled
  const enableCCXRemoval = isFeatureEnabled(Feature.ENABLE_CCX_REMOVAL);
  const ccxFilteredContacts = enableCCXRemoval
    ? holdFilteredContacts.filter(c => !isCCXCategory(c))
    : holdFilteredContacts;

  // Then apply concession compliance filtering
  const preprocessedContacts = concessionFeatureEnabled
    ? ccxFilteredContacts
    : ccxFilteredContacts.filter(c => !isConcessionComplianceCategory(c));

  const [sortedContacts, setSortedContacts] = useState(() =>
    [...preprocessedContacts].sort(
      (noteA: ConsolidatedContact, noteB: ConsolidatedContact) =>
        customSort(sortColumn, sortDirection, noteA, noteB)
    )
  );

  const [kudosContacts, nonKudosContacts] = partitionArray(
    sortedContacts,
    isKudosCategory
  );

  const concessionContactCount = nonKudosContacts.filter(
    (c: ConsolidatedContact) => isConcessionComplianceCategory(c)
  ).length;

  // RECOMMEND_REGULAR_COUNT is concession count + recommend contact size - kudo count
  const RECOMMEND_REGULAR_COUNT =
    // TODO: Need to cap concessionContactCount to RECOMMEND_CONCESSION_COUNT(=5).
    // Currently, we do not have that many number of concession contacts for a CSA.
    // So, allowed all concession contacts for simplicity.
    concessionContactCount +
    (SACoaching
      ? RECOMMEND_SIZE_WITH_SA - RECOMMEND_KUDO_COUNT_WITH_SA
      : RECOMMEND_SIZE - RECOMMEND_KUDO_COUNT);

  const PAGE_SIZE = SACoaching
    ? PAGINATION_SIZE_WITH_SA_RECOMMENDED
    : RECOMMEND_REGULAR_COUNT + RECOMMEND_KUDO_COUNT;
  // Show only top RECOMMEND_REGULAR_COUNT corrective contacts (includes concession contacts) + top kudos contacts
  const reorganizedContacts = [
    ...nonKudosContacts.slice(0, RECOMMEND_REGULAR_COUNT),
    ...(SACoaching
      ? kudosContacts.slice(0, RECOMMEND_KUDO_COUNT_WITH_SA)
      : kudosContacts.slice(0, RECOMMEND_KUDO_COUNT)),
  ];
  const [filteredContacts, setFilteredContacts] = useState(reorganizedContacts);

  const onSort = useCallback(
    ({
      sortColumn,
      sortDirection,
    }: {
      sortColumn: SortableColumn;
      sortDirection: SortableDirection;
    }) => {
      setSortDirection(sortDirection);
      setSortColumn(sortColumn);
      setSortedContacts(
        filteredContacts.sort((a: any, b: any) =>
          customSort(sortColumn, sortDirection, a, b)
        )
      );
    },
    [filteredContacts]
  );

  const [curPage, setCurPage] = useState(1); // 1-based index
  const [pageCount, setPageCount] = useState(
    Math.max(1, Math.ceil(filteredContacts.length / PAGE_SIZE))
  );
  const [resultsCount, setResultsCount] = useState('');

  const [TableSortTrackingTag, tableSortClick] = useCustomTracking(
    'coaching-notes-recommendations-table-sort',
    'coaching-notes-recommendations-table-sort',
    {
      'data-csa-c-type': 'table',
      'data-csa-c-sort-column': sortColumn || '',
      'data-csa-c-sort-order': sortDirection || '',
      'data-csa-c-tag1': sourcePage || '',
      'data-csa-c-tag2': 'Contact Table',
      'data-csa-c-tag3': 'RECOMMENDED',
    }
  );

  useEffect(() => {
    tableSortClick();
  }, [sortColumn, sortDirection]);

  useEffect(() => {
    setSortedContacts(
      [...preprocessedContacts].sort(
        (noteA: ConsolidatedContact, noteB: ConsolidatedContact) =>
          customSort(sortColumn, sortDirection, noteA, noteB)
      )
    );
  }, [preprocessedContacts]);

  useEffect(() => {
    setPageCount(Math.max(1, Math.ceil(filteredContacts.length / PAGE_SIZE)));
    if (filteredContacts.length > 1) {
      setResultsCount(
        filteredContacts.length +
          ' ' +
          intl.formatMessage({
            id: 'cs_perfportal_coaching_note_results',
            defaultMessage: 'results',
          })
      );
    } else {
      setResultsCount(
        filteredContacts.length +
          ' ' +
          intl.formatMessage({
            id: 'cs_perfportal_coaching_note_result',
            defaultMessage: 'result',
          })
      );
    }
  }, [filteredContacts]);

  if (sortedContacts.length === 0) {
    return <NoContactView />;
  }

  return (
    <Column width='100%' alignmentHorizontal='center' spacing='300'>
      <Row width='100%' alignmentHorizontal='start'>
        <Text>
          <FormattedMessage
            id='cs_perfportal_consolidated_recommended_subtitle'
            defaultMessage='These contacts demonstrate coachable behaviors. Save time and select from this list.'
          />
        </Text>
      </Row>
      <Table
        headerRows={1}
        showStripes
        showDividers
        sortColumn={sortColumn}
        sortDirection={
          sortDirection === SortableDirection.ASCENDING
            ? 'ascending'
            : 'descending'
        }
        onSort={onSort}
        data-cy={'recommended-table'}
      >
        <TableSortTrackingTag />
        <TableActionBar>
          <RecommendedTabFilter
            contacts={reorganizedContacts}
            setFilteredContacts={setFilteredContacts}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            sourcePage={sourcePage}
          />
        </TableActionBar>

        {filteredContacts.length === 0 && <EmptyFilterView />}
        {filteredContacts.length !== 0 && (
          <TableRow highlightOnHover={false}>
            <TableCell width='12%' sortColumn={SortableColumn.CONTACT_ID}>
              <FormattedMessage
                id='PerfPortal_contactId'
                defaultMessage='Contact ID'
              />
            </TableCell>
            <TableCell width='12%' sortColumn={SortableColumn.CONTACT_DATE}>
              <FormattedMessage
                id='cs_perfportal_contact_recom_contact_date'
                defaultMessage='Contact date'
              />
            </TableCell>
            <TableCell width='12%' sortColumn={SortableColumn.SIC}>
              <FormattedMessage
                id='cs_perfportal_contact_recom_sic'
                defaultMessage='SIC'
                description='SIC code for recommended contact'
              />
            </TableCell>
            <TableCell width='12%' sortColumn={SortableColumn.CATEGORY}>
              <FormattedMessage
                id='cs_perfportal_category'
                defaultMessage='Category'
              />
            </TableCell>
            <TableCell width='12%' sortColumn={SortableColumn.DURATION}>
              <FormattedMessage
                id='cs_perfportal_duration'
                defaultMessage='Duration'
              />
            </TableCell>
            <TableCell width='12%' sortColumn={SortableColumn.CHANNEL}>
              <FormattedMessage
                id='cs_perfportal_channel'
                defaultMessage='Channel'
              />
            </TableCell>
            <TableCell width='12%' sortColumn={SortableColumn.OWNERSHIP}>
              <FormattedMessage
                id='cs_perfportal_ownership'
                defaultMessage='Ownership'
              />
            </TableCell>
            <TableCell width='12%' sortColumn={SortableColumn.STATUS}>
              <FormattedMessage
                id='cs_perfportal_status'
                defaultMessage='Status'
                description='Status for Contacts'
              />
            </TableCell>
            <TableCell>
              <FormattedMessage
                id='cs_perfportal_actions'
                defaultMessage='Actions'
              />
            </TableCell>
          </TableRow>
        )}

        {filteredContacts.length !== 0 &&
          filteredContacts
            .slice(
              SACoaching
                ? (curPage - 1) * PAGINATION_SIZE_WITH_SA_RECOMMENDED
                : 0,
              SACoaching
                ? curPage * PAGINATION_SIZE_WITH_SA_RECOMMENDED
                : RECOMMEND_REGULAR_COUNT + RECOMMEND_KUDO_COUNT
            )
            .map((contact: ConsolidatedContact, index: number) => {
              const actionStatus = mapToActionStatus(contact);

              const contactInfo: ContactDetails = {
                ...defaultContactDetails,
                ...(contact.contactInfo || {}),
                cscContactID: contact.contactID,
              };

              return (
                <TableRow
                  highlightOnHover={false}
                  key={contact.contactID}
                  data-rank={`${contact.contactID}-rank-${index + 1}`}
                  data-cy={'recommended-contact-row'}
                >
                  <TableCell width='12%'>
                    <Row>
                      <Text>{contact.contactID}</Text>
                    </Row>
                    <Row>
                      <Text>{contact.skill ?? ''}</Text>
                    </Row>
                  </TableCell>
                  <TableCell width='12%'>
                    {moment(contact.contactDate).format('YYYY-MM-DD')}
                  </TableCell>
                  <TableCell width='12%'>
                    <Text>{contact.sic ?? ''}</Text>
                  </TableCell>
                  <TableCell width='12%'>
                    <Text>
                      {concessionFeatureEnabled &&
                      contact.category &&
                      (contact.processName ||
                        contact.concessionFeedbackCommentText) ? (
                        <MoreInfoIcon
                          processName={contact.processName}
                          category={contact.category}
                          concessionFeedbackCommentText={
                            contact.concessionFeedbackCommentText
                          }
                        />
                      ) : (
                        (contact.category ?? '')
                      )}
                    </Text>
                  </TableCell>
                  <TableCell width='12%'>
                    <Text>{formatDuration(contact.duration ?? '')}</Text>
                  </TableCell>
                  <TableCell width='12%'>
                    <Text>
                      <FormattedMessage
                        id={getChannelI18nKey(contact.channel ?? '')}
                        defaultMessage={contact.channel ?? NOT_FOUND_LABEL}
                      />
                    </Text>
                  </TableCell>
                  <TableCell width='12%'>
                    <Text>
                      <FormattedMessage
                        id={getOwnershipI18nKey(contact.ownership ?? '')}
                        defaultMessage={contact.ownership ?? NOT_FOUND_LABEL}
                      />
                    </Text>
                  </TableCell>

                  <TableCell width='12%'>
                    <StatusText
                      data-cy={`contact-status-${contact.contactID}`}
                      status={actionStatus.status}
                      date={actionStatus.date}
                    />
                  </TableCell>
                  <TableCell>
                    <ModalForm
                      parentValues={contact}
                      initialValues={contactInfo}
                      action={actionStatus.action}
                      parentUserName={username}
                      parentUserID={userID}
                      contactTab={ContactViewTabObject.RECOMMENDED}
                      sourcePage={sourcePage}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
      </Table>
      {SACoaching && (
        <Row
          width='50%'
          widths={['grid-4', 'grid-8']}
          alignmentVertical='center'
          spacing='300'
        >
          <Box>
            <Text alignment='right'>{resultsCount}</Text>
          </Box>
          <Box>
            <Pagination
              numberOfPages={pageCount}
              onChange={setCurPage}
              currentPage={curPage}
            />
          </Box>
        </Row>
      )}
    </Column>
  );
};
